import { formatMoney } from 'accounting'
import { DateTime } from 'luxon'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, ListItem, Text, Tag,
} from '@domain-group/suit-theme'

import { buildFullDescription } from '@functions/orderItem'
import { statusColors } from '@functions/status'

import type { UseOrderPayload } from '@hooks/useOrder'
import { useRelations } from '@hooks/useOrderItem'

import type { OrderItemModel, UserModel } from '@models/types'

const getOrdinalSuffix = (day: number) => {
  if (day >= 11 && day <= 13) return 'th'

  switch (day % 10){
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

type LargeListOrderItemProps = {
  currentUser: UserModel,
  isCampaignManager: boolean,
  orderItem: OrderItemModel,
  url: string,
  useOrderPayload: UseOrderPayload,
}

const LargeListOrderItem = (props: LargeListOrderItemProps) => {
  const {
    currentUser, isCampaignManager, orderItem, url, useOrderPayload,
  } = props

  const { id, options_string } = orderItem
  const { status } = useRelations(orderItem)

  const {
    campaign, order: { priority_campaign_user_ids: priorityUserIds, total }, publication, publicationEdition,
  } = useOrderPayload

  const dueDateString = orderItem?.dates?.due?.date || ''
  const date = DateTime.fromFormat(dueDateString, 'dd/MM/yyyy')
  const orderItemDueDate = dueDateString && date.isValid ? `${date.day}${getOrdinalSuffix(date.day)} ${date.toFormat('MMM')}` : null

  // @ts-expect-error TS2739
  const itemTitle = buildFullDescription({ orderItem, publication, publicationEdition })

  // @ts-expect-error TS2322
  const itemTitleWithoutEdition = buildFullDescription({ orderItem, publication })

  const isPriorityCampaignForCurrentUser = priorityUserIds?.includes(currentUser.id)

  return (
    <ListItem
      boxProps={{ as: 'a', borderBottom: '1px solid', padding: 'large' }}
      href={url}
      key={id}
      showAngleIcon
    >
      <Box justifyContent="center" flexDirection="column">
        <Text display={['none', 'inline-flex']} fontSize="small" lineHeight="1.3">
          {isPriorityCampaignForCurrentUser && !isCampaignManager && (
            <Text
              color="blue"
              fontSize="small"
              marginRight="medium"
            >
              <FontAwesomeIcon icon={faStar} />
            </Text>
          )}

          {isCampaignManager ? `${itemTitle}` : campaign?.title}
        </Text>

        <Text display={['inline-flex', 'none']} fontSize="small" lineHeight="1.3">
          {isPriorityCampaignForCurrentUser && !isCampaignManager && (
            <Text
              color="blue"
              fontSize="xsmall"
              marginRight="medium"
            >
              <FontAwesomeIcon icon={faStar} />
            </Text>
          )}

          {isCampaignManager ? `${itemTitleWithoutEdition}` : campaign?.title}
        </Text>

        <Text color="bodyFontLightColor" fontSize="xsmall" lineHeight="1.3" marginTop="small">
          {isCampaignManager ? `${options_string || 'No Options'}` : `${itemTitle}${options_string}`}
        </Text>

        {orderItemDueDate && (
        <Tag boxProps={{ lineHeight: 1.3, marginRight: 'auto', marginTop: 'small' }}>
          Due {orderItemDueDate}
        </Tag>
        )}
      </Box>

      <Box
        alignItems="flex-end"
        flexDirection="column"
        justifyContent="center"
        marginLeft="auto"
        style={{ whiteSpace: 'nowrap' }}
        width="auto"
      >
        {/*
         // @ts-expect-error TS2538 */}
        <Text color={statusColors[status.key]}>{status?.title || 'Pending'}</Text>

        <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium">
          {formatMoney(total)}
        </Text>
      </Box>
    </ListItem>
  )
}

export default LargeListOrderItem
